import { Component } from '@theme/component';

/**
 * @typedef {Object} FlagshipStoresRefs
 * @property {HTMLInputElement[]} storeInputs - Store filter checkboxes
 * @property {HTMLElement} [storeStatus] - Selected store status label
 * @property {HTMLElement} [clearButton] - Clear filter button
 */

/**
 * Filters flagship store content based on sidebar checkbox selections.
 *
 * @extends {Component<FlagshipStoresRefs>}
 */
class FlagshipStoresComponent extends Component {
  static SCROLL_OFFSET_PX = 80;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
    this.#applyFilter();
  }

  /**
   * Clears all selected stores.
   *
   * @param {PointerEvent | KeyboardEvent} event
   */
  clearStores(event) {
    event.preventDefault();

    for (const input of this.refs.storeInputs ?? []) {
      input.checked = false;
    }

    this.#applyFilter();
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'store-filter') return;

    this.#applyFilter();

    event.target.checked && this.#scrollToStore(event.target.value);
  };

  #applyFilter() {
    const checkedInputs = (this.refs.storeInputs ?? []).filter((input) => input.checked);
    const selectedHandles = new Set(checkedInputs.map((input) => input.value));
    const showAll = selectedHandles.size === 0;

    for (const store of this.querySelectorAll('.flagship-store')) {
      store.hidden = !showAll && !selectedHandles.has(store.dataset.storeHandle);
    }

    for (const location of this.querySelectorAll('.flagship-location')) {
      const visibleStores = location.querySelectorAll('.flagship-store:not([hidden])');
      location.hidden = !showAll && visibleStores.length === 0;
    }

    this.#updateStatus(checkedInputs);
    this.#updateClearButton(checkedInputs.length > 0);
  }

  /**
   * @param {HTMLInputElement[]} checkedInputs
   */
  #updateStatus(checkedInputs) {
    const status = this.refs.storeStatus;
    if (!status) return;

    if (checkedInputs.length === 0) {
      status.textContent = '';
      status.classList.remove('bubble', 'facets__bubble');
      return;
    }

    status.textContent =
      checkedInputs.length === 1
        ? checkedInputs[0].dataset.label ?? checkedInputs[0].value
        : String(checkedInputs.length);
    status.classList.toggle('bubble', checkedInputs.length > 1);
    status.classList.toggle('facets__bubble', checkedInputs.length > 1);
  }

  /**
   * @param {boolean} hasSelection
   */
  #updateClearButton(hasSelection) {
    const clearButton = this.refs.clearButton;
    if (!clearButton) return;

    clearButton.hidden = !hasSelection;
    clearButton.classList.toggle('facets__clear--active', hasSelection);
  }

  /**
   * @param {string | undefined} storeHandle
   */
  #scrollToStore(storeHandle) {
    if (!storeHandle) return;

    const store = this.querySelector(`#store-${storeHandle}`);
    if (!(store instanceof HTMLElement)) return;

    const top = store.getBoundingClientRect().top + window.scrollY - FlagshipStoresComponent.SCROLL_OFFSET_PX;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

if (!customElements.get('flagship-stores-component')) {
  customElements.define('flagship-stores-component', FlagshipStoresComponent);
}
